import { BellaTranslator, Logger, appshellCookies, UseAppshellCookies, Config } from '@westwing/appshell_types';
import { AbstractAppBridge } from '@westwing/appshell_types/appBridgeTypes';
import { AppshellMeta } from '@westwing/appshell_types/appshellMetaTypes';
import { AppShellLoginState } from '@westwing/appshell_types/loginStateTypes';
import { FetchingHelpers } from '@westwing/appshell_types/fetchingHelpers';
import * as React from 'react';
import { useAppShell } from './context';
import { FetchedUniversalState, UniversalAction, CustomFeatureToggles } from './types';

export const useLoginState = (): AppShellLoginState => {
    const { loginState } = useAppShell();
    return { ...loginState };
};

export const useAppBridge = (): typeof AbstractAppBridge => {
    const { AppBridge } = useAppShell();
    return AppBridge;
};

export const useConfig = (): Config => {
    const { config } = useAppShell();
    return { ...config };
};

export const useAppshellMeta = (): AppshellMeta => {
    const { appshellMeta } = useAppShell();
    return { ...appshellMeta };
};

export const useTranslator = (): BellaTranslator => {
    const { t } = useAppShell();
    return t;
};

export const useLogger = (): Logger => {
    const { logger } = useAppShell();
    return logger;
};

export const useFeatureToggle = (): CustomFeatureToggles => {
    const { featureToggles } = useAppShell();
    return { ...featureToggles };
};

export const useUniversalFetch = <T extends UniversalAction>(
    action: T,
    executor: () => PromiseLike<FetchedUniversalState[T] | undefined>,
    deps?: unknown[]
): void => {
    const { useUniversalFetch: useAppShellUniversalFetch } = useAppShell();

    return useAppShellUniversalFetch(action, executor, deps);
};

export type MFCookiesNames = appshellCookies | 'im_example_cookie_plz_replace_me_senpai';

export const useCookies = <CookieValue = unknown, SetFnCookieValue = unknown>(
    name: MFCookiesNames,
    shouldConvertToBoolean?: true
): ReturnType<UseAppshellCookies<CookieValue, SetFnCookieValue, MFCookiesNames>> => {
    const { useCookies: useAppshellCookies } = useAppShell();
    return useAppshellCookies(name as appshellCookies, shouldConvertToBoolean);
};

// This is how Function Overloads looks like in TS. The first two lines are exported types
// 3rd line - is an implementation, which signature matches both types and is not exported outside
// https://www.typescriptlang.org/docs/handbook/2/functions.html#function-overloads
function useFetchedUniversalState(): FetchedUniversalState;
function useFetchedUniversalState<T extends UniversalAction>(action: T): FetchedUniversalState[T];
function useFetchedUniversalState<T extends UniversalAction>(action?: T): FetchedUniversalState[T] {
    const { fetchedUniversalState } = useAppShell();

    return action ? fetchedUniversalState?.[action] : fetchedUniversalState || {};
}
export { useFetchedUniversalState };

export const useUniversalFetchingHelpers = (): FetchingHelpers => {
    const { fetchingHelpers } = useAppShell();

    const refHelpers = React.useRef(fetchingHelpers);

    return refHelpers.current;
};
